if (!customElements.get('kivo-home-mode-control')) {
  class KivoHomeModeControl extends HTMLElement {
    constructor() {
      super();
      this.rail = null;
      this.tabs = [];
      this.panels = [];
      this.arrows = [];
      this.activeIndex = 0;
      this.scrollFrame = null;
      this.handleResize = this.alignActivePanel.bind(this, false);
      this.handleRailScrollBound = this.handleRailScroll.bind(this);
      this.tabClickHandlers = new Map();
      this.tabKeydownHandlers = new Map();
      this.arrowClickHandlers = new Map();
    }

    connectedCallback() {
      this.rail = this.querySelector('[data-kivo-home-mode-rail]');
      this.tabs = Array.from(this.querySelectorAll('[data-kivo-home-mode-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-kivo-home-mode-panel]'));
      this.arrows = Array.from(this.querySelectorAll('[data-kivo-home-mode-arrow]'));

      if (!this.rail || !this.tabs.length || !this.panels.length) return;

      const configuredIndex = Number.parseInt(this.dataset.activeIndex || '0', 10);
      this.activeIndex = Number.isFinite(configuredIndex)
        ? Math.min(Math.max(configuredIndex, 0), this.panels.length - 1)
        : 0;

      this.tabs.forEach((tab) => {
        const clickHandler = this.handleTabClick.bind(this);
        const keydownHandler = this.handleTabKeydown.bind(this);
        this.tabClickHandlers.set(tab, clickHandler);
        this.tabKeydownHandlers.set(tab, keydownHandler);
        tab.addEventListener('click', clickHandler);
        tab.addEventListener('keydown', keydownHandler);
      });

      this.arrows.forEach((arrow) => {
        const clickHandler = this.handleArrowClick.bind(this);
        this.arrowClickHandlers.set(arrow, clickHandler);
        arrow.addEventListener('click', clickHandler);
      });

      this.rail.addEventListener('scroll', this.handleRailScrollBound, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });

      this.setActive(this.activeIndex, false, false);
      requestAnimationFrame(() => this.alignActivePanel(false));
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      if (this.rail) this.rail.removeEventListener('scroll', this.handleRailScrollBound);
      this.tabs.forEach((tab) => {
        const clickHandler = this.tabClickHandlers.get(tab);
        const keydownHandler = this.tabKeydownHandlers.get(tab);
        if (clickHandler) tab.removeEventListener('click', clickHandler);
        if (keydownHandler) tab.removeEventListener('keydown', keydownHandler);
      });
      this.tabClickHandlers.clear();
      this.tabKeydownHandlers.clear();
      this.arrows.forEach((arrow) => {
        const clickHandler = this.arrowClickHandlers.get(arrow);
        if (clickHandler) arrow.removeEventListener('click', clickHandler);
      });
      this.arrowClickHandlers.clear();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    handleTabClick(event) {
      const index = Number.parseInt(event.currentTarget.dataset.modeIndex, 10);
      this.setActive(index, true, false);
    }

    handleTabKeydown(event) {
      const current = Number.parseInt(event.currentTarget.dataset.modeIndex, 10);
      let next = current;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      if (next === current) return;

      event.preventDefault();
      this.tabs[next].focus();
      this.setActive(next, true, false);
    }

    handleArrowClick(event) {
      const direction = Number.parseInt(event.currentTarget.dataset.kivoHomeModeArrow || '0', 10);
      if (!direction || !this.panels.length) return;

      const next = (this.activeIndex + direction + this.panels.length) % this.panels.length;
      this.setActive(next, true, false);
    }

    handleRailScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        const railCenter = this.rail.scrollLeft + this.rail.clientWidth / 2;
        let closestIndex = this.activeIndex;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.panels.forEach((panel, index) => {
          const panelCenter = panel.offsetLeft + panel.offsetWidth / 2;
          const distance = Math.abs(panelCenter - railCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        if (closestIndex !== this.activeIndex) this.setActive(closestIndex, false, false);
      });
    }

    setActive(index, shouldScroll, shouldFocus) {
      if (!Number.isFinite(index) || !this.panels[index]) return;
      this.activeIndex = index;
      this.dataset.activeIndex = String(index);

      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && shouldFocus) tab.focus();
      });

      this.panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });

      if (shouldScroll) this.alignActivePanel(true);
    }

    alignActivePanel(smooth = false) {
      if (!this.rail || !this.panels[this.activeIndex]) return;
      const panel = this.panels[this.activeIndex];
      const left = panel.offsetLeft - (this.rail.clientWidth - panel.offsetWidth) / 2;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.rail.scrollTo({ left, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
    }
  }

  customElements.define('kivo-home-mode-control', KivoHomeModeControl);
}
